import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import { from, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import {
    ApiFragment,
    defaultNavigationOptions,
    NavigationOptions,
    RecordNavigationService,
    RecordService,
    RecordStatus,
    RecordType,
    ToastHelper,
    VersioningService,
    WorkflowTask
} from '@digihaven/shared';

import { Dossier } from '@common/entities/dossier/dossier.entity';

@Injectable()
export class NavigationService extends RecordNavigationService {
    constructor(
        protected recordService: RecordService,
        protected router: Router,
        versioningService: VersioningService,
        protected toastHelper: ToastHelper
    ) {
        super(recordService, versioningService, router, toastHelper);
    }

    navigate(record: ApiFragment | Dossier, navigationOptions: NavigationOptions = defaultNavigationOptions) {
        if (record instanceof ApiFragment) {
            return this.navigateTo(record, navigationOptions);
        } else {
            return this.navigateToDossierWithOptions(record, navigationOptions);
        }
    }

    navigateTo(
        record: ApiFragment,
        navigationOptions: NavigationOptions = defaultNavigationOptions
    ): Observable<boolean> {
        switch (record.getRecordType()) {
            case RecordType.Sip:
                return this.navigateToSip(record, navigationOptions);
            case RecordType.SipData:
                return this.navigateToSip(record, navigationOptions);
            case RecordType.Dossier:
                return this.navigateToDossier(record, navigationOptions);
            case RecordType.Document:
                return this.navigateToDocument(record, navigationOptions);
            case RecordType.Representation:
                return this.navigateToRepresentation(record, navigationOptions);
            case RecordType.OrphanDocument:
                return this.navigateToOrphanDocument(record, navigationOptions);
            case RecordType.DepotRequest:
                return this.navigateToDepotRequest(record, navigationOptions);
            default:
                return this.handleNavigationError(
                    `Unsupported record type: ${record.getRecordType()}`,
                    navigationOptions
                );
        }
    }

    private navigateToDossierWithOptions(
        dossier: Dossier,
        navigationOptions: NavigationOptions = defaultNavigationOptions
    ): Observable<boolean> {
        return this.recordService
            .getByRecordId(dossier.Id!)
            .pipe(switchMap(record => this.navigateTo(record, navigationOptions)));
    }

    private navigateToSip(record: ApiFragment, navigationOptions: NavigationOptions): Observable<boolean> {
        return from(
            this.router.navigate(['/input', 'processing-list', record.getRecordId()], {
                queryParams: navigationOptions.queryParams
            })
        );
    }

    private navigateToDossier(record: ApiFragment, navigationOptions: NavigationOptions): Observable<boolean> {
        const additionalNavigationParts = navigationOptions.editMode
            ? [this.getTabOrDefault(navigationOptions), 'edit']
            : navigationOptions.tab
              ? [navigationOptions.tab]
              : [];

        const routerCommands: string[] = [
            ...this.getDossierNavPart(record.getStatus(), record.getMediaObjectId(), this.getTaskList(record)),
            ...additionalNavigationParts
        ];

        const routerNavigationOptions: NavigationExtras = {
            ...(!!navigationOptions.state && { state: navigationOptions.state }),
            queryParams: navigationOptions.queryParams
        };

        let routerPromise: Promise<boolean>;

        if (Object.keys(routerNavigationOptions).length > 0) {
            routerPromise = this.router.navigate(routerCommands, routerNavigationOptions);
        } else {
            routerPromise = this.router.navigate(routerCommands, { queryParams: navigationOptions.queryParams });
        }

        return from(routerPromise);
    }

    private getTabOrDefault(navigationOptions: NavigationOptions): string {
        if (navigationOptions.tab) {
            return navigationOptions.tab;
        }

        return 'details';
    }

    private navigateToDocument(record: ApiFragment, navigationOptions: NavigationOptions): Observable<boolean> {
        const isCandidateChildOfDossierId: string | undefined = record.metadata.Structural.Relations?.IsCandidateChildOf
            ? record.metadata.Structural.Relations?.IsCandidateChildOf[0]
            : undefined;
        if (isCandidateChildOfDossierId) {
            return this.navigateToDocumentsToArchiveDetail(isCandidateChildOfDossierId, record, navigationOptions);
        } else {
            return this.navigateToDocumentUnderDossier(
                record.metadata.Structural.ReferenceCodes.Dossier[0],
                record.getMediaObjectId(),
                navigationOptions
            );
        }
    }

    private navigateToDocumentsToArchiveDetail(
        isCandidateChildOfDossierId: string,
        record: ApiFragment,
        navigationOptions: NavigationOptions
    ): Observable<boolean> {
        return this.recordService.getByRecordId(isCandidateChildOfDossierId).pipe(
            switchMap(dossier => {
                const routeParts = [
                    '/input',
                    'documents-to-archive',
                    dossier.getMediaObjectId(),
                    'documents',
                    record.getMediaObjectId(),
                    ...(navigationOptions.tab ? [navigationOptions.tab] : []),
                    ...(navigationOptions.editMode ? ['edit'] : [])
                ];
                return this.router.navigate(routeParts, { queryParams: navigationOptions.queryParams });
            }),
            catchError(err => this.handleNavigationError(err, navigationOptions))
        );
    }

    private navigateToRepresentation(record: ApiFragment, navigationOptions: NavigationOptions): Observable<boolean> {
        if (record.hasReferenceCode(RecordType.Dossier)) {
            return this.navigateToRepresentationUnderDossier(
                record.metadata.Structural.ReferenceCodes.Dossier[0],
                record.metadata.Structural.ReferenceCodes.Document[0],
                record.getRecordId(),
                navigationOptions
            );
        } else if (record.hasReferenceCode(RecordType.Classification)) {
            return this.navigateToRepresentationUnderOrphanedDocument(
                record,
                navigationOptions,
                record.metadata.Structural.ReferenceCodes.Document[0],
                record.getRecordId()
            );
        } else {
            return from(
                this.router.navigate(
                    [
                        'input',
                        'concepts',
                        'all',
                        'documents',
                        record.getReferenceCode(RecordType.Document),
                        'representations',
                        record.getRecordId()
                    ],
                    {
                        queryParams: navigationOptions.queryParams
                    }
                )
            ).pipe(catchError(err => this.handleNavigationError(err, navigationOptions)));
        }
    }

    private navigateToDocumentUnderDossier(
        dossierId: string,
        documentId: string,
        navigationOptions: NavigationOptions
    ): Observable<boolean> {
        return this.recordService.getByRecordId(dossierId).pipe(
            switchMap(dossier => {
                return this.router.navigate(this.getDocumentNavPart(dossier, documentId, navigationOptions), {
                    queryParams: navigationOptions.queryParams
                });
            }),
            catchError(err => this.handleNavigationError(err, navigationOptions))
        );
    }

    private navigateToRepresentationUnderDossier(
        dossierId: string,
        documentId: string,
        representationId: string,
        navigationOptions: NavigationOptions
    ): Observable<boolean> {
        return this.recordService.getByRecordId(dossierId).pipe(
            switchMap(dossier => {
                return this.router.navigate(
                    [...this.getDocumentNavPart(dossier, documentId), 'representations', representationId],
                    { queryParams: navigationOptions.queryParams }
                );
            }),
            catchError(err => this.handleNavigationError(err, navigationOptions))
        );
    }

    private navigateToRepresentationUnderOrphanedDocument(
        record: ApiFragment,
        navigationOptions: NavigationOptions,
        documentId: string,
        representationId: string
    ): Observable<boolean> {
        const route = [...this.getOrphanDocumentNavPart(record, documentId), 'representations', representationId];
        return from(this.router.navigate(route, { queryParams: navigationOptions.queryParams })).pipe(
            catchError(err => this.handleNavigationError(err, navigationOptions))
        );
    }

    private navigateToOrphanDocument(
        record: ApiFragment,
        navigationOptions: NavigationOptions,
        documentId?: string
    ): Observable<boolean> {
        const orphanedDocumentId = documentId ? documentId : record.getMediaObjectId();
        const route = this.getOrphanDocumentNavPart(record, orphanedDocumentId);
        return from(this.router.navigate(route, { queryParams: navigationOptions.queryParams })).pipe(
            catchError(err => this.handleNavigationError(err, navigationOptions))
        );
    }

    private navigateToDepotRequest(record: ApiFragment, navigationOptions: NavigationOptions): Observable<boolean> {
        return from(
            this.router.navigate(['/depot-requests', record.getRecordId()], {
                queryParams: navigationOptions.queryParams
            })
        );
    }

    private getDossierNavPart(status: string | null, id: string, tasks: WorkflowTask[]): string[] {
        switch (status) {
            case RecordStatus.DraftValid:
            case RecordStatus.DraftInvalid:
                return ['/input', 'concepts', 'all', id];
            case RecordStatus.Rejected:
                return ['/input', 'concepts', 'rejected', id];
            case RecordStatus.Submitted:
            case RecordStatus.RejectedForCorrection:
                return this.navigateToTask(id, tasks);
            default:
                return ['/archive', 'all', id];
        }
    }

    private navigateToTask(id: string, tasks?: WorkflowTask[]): string[] {
        if (!tasks || tasks.length === 0 || tasks[0].TypeId === null) {
            return ['/input', 'concepts', id];
        }

        const task: WorkflowTask = tasks[0];
        return ['/evaluate', 'tasks', task.Id, id];
    }

    private getDocumentNavPart(
        dossier: ApiFragment,
        documentId: string,
        navigationOptions: NavigationOptions = defaultNavigationOptions
    ): string[] {
        return [
            ...this.getDossierNavPart(dossier.getStatus(), dossier.getMediaObjectId(), this.getTaskList(dossier)),
            'documents',
            documentId,
            ...(navigationOptions.editMode ? ['details', 'edit'] : [])
        ];
    }

    private getOrphanDocumentNavPart(orphanDocument: ApiFragment, documentId: string): string[] {
        const classificationId: string = orphanDocument.metadata.Structural.ReferenceCodes.Classification[0];

        const mainRoute: string =
            orphanDocument.getStatus() === RecordStatus.Published ? '/archive' : '/input/concepts';

        return [mainRoute, 'classifications', classificationId, 'documents', documentId];
    }

    private getTaskList(dossier: ApiFragment): WorkflowTask[] {
        return dossier.metadata.Workflow?.Tasks?.Task || [];
    }
}
